// 增强存储服务 - 支持大容量存储，不限制单个故事大小
// 核心原则：只要总空间够，就不限制单个故事
// 使用 IndexedDB 提供更大的存储空间（通常几百MB到几GB）

class EnhancedStorageService {
  constructor() {
    this.dbName = 'AltverseDB'
    this.dbVersion = 6  // 版本升级：添加世界模拟器存储表（已升级到6以兼容之前的自动升级）
    this.db = null
    this.initRetryCount = 0  // 初始化重试计数，防止无限循环
    this.maxInitRetries = 3  // 最大重试次数
    this.initPromise = this.initDB()
  }

  /**
   * 初始化 IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)

      request.onerror = () => {
        const error = request.error
        // 如果是因为版本号小于现有版本，需要升级到更高版本
        if (error.name === 'VersionError' && error.message.includes('less than the existing version')) {
          console.warn('⚠️ 请求的版本号小于现有版本，需要升级版本号...')
          
          // 先不提供版本号打开，获取现有版本
          const checkRequest = indexedDB.open(this.dbName)
          checkRequest.onsuccess = () => {
            const db = checkRequest.result
            const existingVersion = db.version
            console.log(`📊 检测到现有数据库版本: ${existingVersion}`)
            db.close()
            
            // 使用现有版本+1 来触发升级（如果需要创建缺失的对象存储）
            this.dbVersion = existingVersion + 1
            console.log(`🔄 升级到版本 ${this.dbVersion} 以检查并创建缺失的对象存储...`)
            
            // 重置重试计数
            this.initRetryCount = 0
            
            // 重新初始化（这会触发 onupgradeneeded 如果版本号增加）
            this.initDB().then(resolve).catch(reject)
          }
          checkRequest.onerror = () => {
            console.error('❌ 无法检查现有数据库版本:', checkRequest.error)
            reject(error)
          }
          return
        }
        
        console.error('❌ IndexedDB 打开失败:', error)
        reject(error)
      }

      request.onsuccess = () => {
        this.db = request.result
        
        // 检查所有必需的对象存储是否存在
        const requiredStores = [
          'stories', 'images', 'metadata', 'chatSessions', 'characterProfiles',
          'characterStates', 'characterDiaries', 'userGifts', 'giftCatalog',
          'chatSummaries', 'characterMemories', 'characterClothing', 'characterExpressions',
          'worldSimulators', 'worldSessions', 'psychProfiles'
        ]
        
        const missingStores = requiredStores.filter(storeName => !this.db.objectStoreNames.contains(storeName))
        
        if (missingStores.length > 0) {
          if (this.initRetryCount >= this.maxInitRetries) {
            console.error('❌ 数据库初始化失败：已达到最大重试次数，缺少对象存储:', missingStores)
            reject(new Error(`数据库初始化失败：缺少对象存储 ${missingStores.join(', ')}。请手动刷新页面（Ctrl+F5 或 Cmd+Shift+R）`))
            return
          }
          
          console.warn('⚠️ 数据库缺少必需的对象存储:', missingStores, '，需要升级...')
          // 关闭当前数据库连接
          this.db.close()
          this.db = null
          
          // 增加版本号以触发升级
          this.initRetryCount++
          this.dbVersion = this.dbVersion + 1
          console.log(`🔄 升级数据库版本到 ${this.dbVersion} 以创建缺失的对象存储... (重试 ${this.initRetryCount}/${this.maxInitRetries})`)
          
          // 重新初始化（这会触发 onupgradeneeded）
          this.initDB().then(resolve).catch(reject)
          return
        }
        
        // 重置重试计数（成功初始化后）
        this.initRetryCount = 0
        
        console.log('✅ IndexedDB 已初始化，所有对象存储都存在')
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        const transaction = event.target.transaction

        // 创建故事存储
        if (!db.objectStoreNames.contains('stories')) {
          const storyStore = db.createObjectStore('stories', { keyPath: 'id' })
          storyStore.createIndex('userId', 'userId', { unique: false })
          storyStore.createIndex('createdAt', 'createdAt', { unique: false })
          console.log('📦 创建 stories 存储')
        }

        // 创建图片存储
        if (!db.objectStoreNames.contains('images')) {
          const imageStore = db.createObjectStore('images', { keyPath: 'id' })
          imageStore.createIndex('storyId', 'storyId', { unique: false })
          console.log('🖼️ 创建 images 存储')
        }

        // 创建元数据存储
        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' })
          console.log('📊 创建 metadata 存储')
        }

        // 创建聊天会话存储（玩创模式）
        if (!db.objectStoreNames.contains('chatSessions')) {
          const chatStore = db.createObjectStore('chatSessions', { keyPath: 'id' })
          chatStore.createIndex('characterId', 'characterId', { unique: false })
          chatStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          console.log('💬 创建 chatSessions 存储')
        }

        // 创建角色档案存储（玩创模式）
        if (!db.objectStoreNames.contains('characterProfiles')) {
          const charStore = db.createObjectStore('characterProfiles', { keyPath: 'id' })
          charStore.createIndex('createdAt', 'createdAt', { unique: false })
          console.log('👤 创建 characterProfiles 存储')
        }

        // 创建角色状态存储（玩创模式）
        if (!db.objectStoreNames.contains('characterStates')) {
          const stateStore = db.createObjectStore('characterStates', { keyPath: 'characterId' })
          stateStore.createIndex('favorability', 'favorability', { unique: false })
          stateStore.createIndex('lastChatTime', 'lastChatTime', { unique: false })
          console.log('📊 创建 characterStates 存储')
        }

        // 创建角色日记存储（玩创模式）
        if (!db.objectStoreNames.contains('characterDiaries')) {
          const diaryStore = db.createObjectStore('characterDiaries', { keyPath: 'id' })
          diaryStore.createIndex('characterId', 'characterId', { unique: false })
          diaryStore.createIndex('date', 'date', { unique: false })
          diaryStore.createIndex('favorabilityRequirement', 'favorabilityRequirement', { unique: false })
          console.log('📔 创建 characterDiaries 存储')
        }

        // 创建用户礼物库存存储（玩创模式）
        if (!db.objectStoreNames.contains('userGifts')) {
          const giftStore = db.createObjectStore('userGifts', { keyPath: 'userId' })
          console.log('🎁 创建 userGifts 存储')
        }

        // 创建礼物目录存储（玩创模式）
        if (!db.objectStoreNames.contains('giftCatalog')) {
          const catalogStore = db.createObjectStore('giftCatalog', { keyPath: 'id' })
          catalogStore.createIndex('rarity', 'rarity', { unique: false })
          catalogStore.createIndex('price', 'price', { unique: false })
          console.log('🛍️ 创建 giftCatalog 存储')
        }

        // 创建聊天摘要存储（长期记忆）
        if (!db.objectStoreNames.contains('chatSummaries')) {
          const summaryStore = db.createObjectStore('chatSummaries', { keyPath: 'id' })
          summaryStore.createIndex('characterId', 'characterId', { unique: false })
          summaryStore.createIndex('sessionId', 'sessionId', { unique: false })
          summaryStore.createIndex('timestamp', 'timestamp', { unique: false })
          console.log('📝 创建 chatSummaries 存储')
        }

        // 创建角色记忆点存储（长期记忆）
        if (!db.objectStoreNames.contains('characterMemories')) {
          const memoryStore = db.createObjectStore('characterMemories', { keyPath: 'id' })
          memoryStore.createIndex('characterId', 'characterId', { unique: false })
          memoryStore.createIndex('importance', 'importance', { unique: false })
          memoryStore.createIndex('timestamp', 'timestamp', { unique: false })
          console.log('🧠 创建 characterMemories 存储')
        }

        // 创建角色服装库存储（换装系统）
        if (!db.objectStoreNames.contains('characterClothing')) {
          const clothingStore = db.createObjectStore('characterClothing', { keyPath: 'id' })
          clothingStore.createIndex('characterId', 'characterId', { unique: false })
          clothingStore.createIndex('category', 'category', { unique: false })
          console.log('👔 创建 characterClothing 存储')
        }

        // 创建角色表情存储（表情系统）
        if (!db.objectStoreNames.contains('characterExpressions')) {
          const expressionStore = db.createObjectStore('characterExpressions', { keyPath: 'id' })
          expressionStore.createIndex('characterId', 'characterId', { unique: false })
          expressionStore.createIndex('expressionType', 'expressionType', { unique: false })
          console.log('😊 创建 characterExpressions 存储')
        }

        // 创建世界模拟器存储（世界模拟器模式）
        if (!db.objectStoreNames.contains('worldSimulators')) {
          const worldStore = db.createObjectStore('worldSimulators', { keyPath: 'worldId' })
          worldStore.createIndex('creatorId', 'creatorId', { unique: false })
          worldStore.createIndex('createdAt', 'createdAt', { unique: false })
          worldStore.createIndex('isPublic', 'isPublic', { unique: false })
          worldStore.createIndex('isFeatured', 'isFeatured', { unique: false })
          worldStore.createIndex('playCount', 'playCount', { unique: false })
          worldStore.createIndex('rating', 'rating', { unique: false })
          console.log('🌍 创建 worldSimulators 存储')
        }

        // 创建世界会话存储（世界模拟器模式）
        if (!db.objectStoreNames.contains('worldSessions')) {
          const sessionStore = db.createObjectStore('worldSessions', { keyPath: 'sessionId' })
          sessionStore.createIndex('worldId', 'worldId', { unique: false })
          sessionStore.createIndex('userId', 'userId', { unique: false })
          sessionStore.createIndex('startTime', 'startTime', { unique: false })
          sessionStore.createIndex('status', 'status', { unique: false })
          console.log('🎮 创建 worldSessions 存储')
        }

        // 创建心理档案存储（世界模拟器模式）
        if (!db.objectStoreNames.contains('psychProfiles')) {
          const profileStore = db.createObjectStore('psychProfiles', { keyPath: 'userId' })
          profileStore.createIndex('createdAt', 'createdAt', { unique: false })
          profileStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          console.log('🧠 创建 psychProfiles 存储')
        }
      }
    })
  }

  /**
   * 等待 DB 初始化完成（带超时保护）
   */
  async ensureDB() {
    if (!this.db) {
      // 添加10秒超时保护，避免IndexedDB初始化卡死
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('IndexedDB初始化超时')), 10000)
      )
      
      try {
        await Promise.race([this.initPromise, timeoutPromise])
      } catch (error) {
        console.error('❌ IndexedDB初始化失败:', error)
        throw error
      }
    }
    
    if (!this.db) {
      throw new Error('IndexedDB未能正确初始化')
    }
    
    return this.db
  }

  /**
   * 保存故事（不限制大小，带重试机制，保证数据完整性）
   * @param {Object} story - 故事数据
   * @param {Object} options - 选项 { retryCount: 重试次数, timeout: 超时时间 }
   * @returns {Promise<void>}
   */
  async saveStory(story, options = {}) {
    const { retryCount = 3, timeout = 30000 } = options // 默认重试3次，30秒超时
    
    let lastError = null
    
    // 重试机制：确保数据一定能保存
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        console.log(`📝 尝试保存故事 (${attempt}/${retryCount}):`, story.id)
        
        // 确保DB初始化
        await this.ensureDB()

        const transaction = this.db.transaction(['stories'], 'readwrite')
        const store = transaction.objectStore('stories')

        // 添加时间戳
        const storyData = {
          ...story,
          tags: story.tags || [], // 标签
          updatedAt: new Date().toISOString(),
          savedAt: Date.now()
        }

        // 保存操作（给予充足的时间）
        const savePromise = new Promise((resolve, reject) => {
          const request = store.put(storyData)
          
          request.onsuccess = () => {
            console.log(`✅ 故事已保存到 IndexedDB (尝试 ${attempt}):`, story.id)
            resolve()
          }
          
          request.onerror = () => {
            console.error(`❌ 保存请求失败 (尝试 ${attempt}):`, request.error)
            reject(request.error)
          }
          
          // 监听事务状态
          transaction.oncomplete = () => {
            console.log(`✅ 事务完成 (尝试 ${attempt})`)
            resolve()
          }
          
          transaction.onerror = () => {
            console.error(`❌ 事务错误 (尝试 ${attempt}):`, transaction.error)
            reject(transaction.error)
          }
          
          transaction.onabort = () => {
            console.error(`❌ 事务被中止 (尝试 ${attempt})`)
            reject(new Error('事务被中止'))
          }
        })
        
        // 超时保护（但给予充足的时间）
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('保存操作超时')), timeout)
        )
        
        await Promise.race([savePromise, timeoutPromise])
        
        // 成功保存，返回
        console.log(`✅ 故事保存成功:`, story.id, `数据大小: ${this.getDataSize(story)}`)
        return true
        
      } catch (error) {
        lastError = error
        console.error(`❌ 保存失败 (尝试 ${attempt}/${retryCount}):`, error)
        
        // 如果不是最后一次尝试，等待后重试
        if (attempt < retryCount) {
          const waitTime = attempt * 1000 // 递增等待时间：1秒、2秒、3秒
          console.log(`⏳ 等待 ${waitTime}ms 后重试...`)
          await new Promise(resolve => setTimeout(resolve, waitTime))
          
          // 如果是初始化错误，重置DB
          if (error.message.includes('初始化')) {
            console.log('🔄 重置IndexedDB连接...')
            this.db = null
            this.initPromise = this.initDB()
          }
        }
      }
    }
    
    // 所有重试都失败了
    console.error(`❌ 保存故事失败，已重试${retryCount}次:`, story.id, lastError)
    
    // 不降级，而是将数据放入待保存队列
    this.addToSaveQueue(story)
    
    throw new Error(`保存失败（已重试${retryCount}次）: ${lastError.message}`)
  }

  /**
   * 获取数据大小（用于日志）
   */
  getDataSize(data) {
    try {
      const size = new Blob([JSON.stringify(data)]).size
      return this.formatBytes(size)
    } catch (error) {
      return '未知'
    }
  }

  /**
   * 待保存队列（用于失败后的后台重试）
   */
  saveQueue = []
  saveQueueProcessing = false

  /**
   * 添加到保存队列
   */
  addToSaveQueue(story) {
    console.log('📋 添加到保存队列:', story.id)
    
    // 检查队列中是否已存在该故事
    const existingIndex = this.saveQueue.findIndex(item => item.id === story.id)
    if (existingIndex >= 0) {
      // 更新队列中的数据
      this.saveQueue[existingIndex] = story
      console.log('📋 更新队列中的故事:', story.id)
    } else {
      // 添加新的
      this.saveQueue.push(story)
      console.log('📋 队列长度:', this.saveQueue.length)
    }
    
    // 触发后台处理
    this.processSaveQueue()
  }

  /**
   * 处理保存队列（后台持续重试）
   */
  async processSaveQueue() {
    if (this.saveQueueProcessing) {
      return // 已经在处理中
    }
    
    this.saveQueueProcessing = true
    
    while (this.saveQueue.length > 0) {
      const story = this.saveQueue[0]
      
      try {
        console.log(`🔄 从队列重试保存故事: ${story.id}`)
        
        // 尝试保存（单次尝试，超时时间更长）
        await this.saveStory(story, { retryCount: 1, timeout: 60000 })
        
        // 成功，从队列移除
        this.saveQueue.shift()
        console.log(`✅ 队列中的故事保存成功: ${story.id}`)
        
      } catch (error) {
        console.error(`❌ 队列重试失败: ${story.id}`, error)
        
        // 等待更长时间后再重试
        await new Promise(resolve => setTimeout(resolve, 10000)) // 10秒
      }
    }
    
    this.saveQueueProcessing = false
    console.log('✅ 保存队列处理完成')
  }

  /**
   * 获取保存队列状态
   */
  getSaveQueueStatus() {
    return {
      pending: this.saveQueue.length,
      processing: this.saveQueueProcessing,
      stories: this.saveQueue.map(s => ({ id: s.id, title: s.title }))
    }
  }

  /**
   * 备份到 localStorage（仅基本信息）
   */
  backupToLocalStorage(story) {
    try {
      const basicInfo = {
        id: story.id,
        title: story.title,
        description: story.description,
        genre: story.genre,
        createdAt: story.createdAt,
        updatedAt: story.updatedAt,
        status: story.status
      }

      const storiesKey = 'altverse-stories-index'
      const existingIndex = JSON.parse(localStorage.getItem(storiesKey) || '[]')
      const updatedIndex = existingIndex.filter(s => s.id !== story.id)
      updatedIndex.push(basicInfo)

      localStorage.setItem(storiesKey, JSON.stringify(updatedIndex))
      console.log('💾 已备份故事索引到 localStorage')
    } catch (error) {
      console.warn('⚠️ localStorage 备份失败（不影响主存储）:', error)
    }
  }

  /**
   * 获取故事
   * @param {string} storyId - 故事ID
   * @returns {Promise<Object>}
   */
  async getStory(storyId) {
    try {
      await this.ensureDB()

      const transaction = this.db.transaction(['stories'], 'readonly')
      const store = transaction.objectStore('stories')

      return new Promise((resolve, reject) => {
        const request = store.get(storyId)
        request.onsuccess = () => {
          resolve(request.result || null)
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('❌ 获取故事失败:', error)
      return null
    }
  }

  /**
   * 获取所有故事
   * @param {string} userId - 用户ID
   * @returns {Promise<Array>}
   */
  async getAllStories(userId = 'guest') {
    try {
      await this.ensureDB()

      const transaction = this.db.transaction(['stories'], 'readonly')
      const store = transaction.objectStore('stories')
      const index = store.index('userId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId)
        request.onsuccess = () => {
          const stories = request.result || []
          console.log(`📚 加载了 ${stories.length} 个故事`)
          resolve(stories)
        }
        request.onerror = () => {
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('❌ 获取所有故事失败:', error)
      return []
    }
  }

  /**
   * 删除故事
   * @param {string} storyId - 故事ID
   */
  async deleteStory(storyId) {
    try {
      await this.ensureDB()

      const transaction = this.db.transaction(['stories'], 'readwrite')
      const store = transaction.objectStore('stories')

      await new Promise((resolve, reject) => {
        const request = store.delete(storyId)
        request.onsuccess = () => {
          console.log('🗑️ 故事已删除:', storyId)
          resolve()
        }
        request.onerror = () => {
          reject(request.error)
        }
      })

      // 同时从 localStorage 索引中删除
      try {
        const storiesKey = 'altverse-stories-index'
        const existingIndex = JSON.parse(localStorage.getItem(storiesKey) || '[]')
        const updatedIndex = existingIndex.filter(s => s.id !== storyId)
        localStorage.setItem(storiesKey, JSON.stringify(updatedIndex))
      } catch (error) {
        console.warn('⚠️ localStorage 索引更新失败')
      }
    } catch (error) {
      console.error('❌ 删除故事失败:', error)
      throw error
    }
  }

  /**
   * 获取存储使用情况
   * @returns {Promise<Object>}
   */
  async getStorageInfo() {
    try {
      // 检查 IndexedDB 配额
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate()
        const usage = estimate.usage || 0
        const quota = estimate.quota || 0

        return {
          used: usage,
          total: quota,
          available: quota - usage,
          percentage: quota > 0 ? ((usage / quota) * 100).toFixed(2) : 0,
          formattedUsed: this.formatBytes(usage),
          formattedTotal: this.formatBytes(quota),
          formattedAvailable: this.formatBytes(quota - usage),
          type: 'IndexedDB',
          unlimited: quota > 100 * 1024 * 1024 * 1024 // 大于100GB视为无限制
        }
      }

      // 降级到估算
      return {
        used: 0,
        total: 50 * 1024 * 1024, // 假设50MB
        available: 50 * 1024 * 1024,
        percentage: 0,
        formattedUsed: '0 B',
        formattedTotal: '50 MB',
        formattedAvailable: '50 MB',
        type: 'Estimated'
      }
    } catch (error) {
      console.error('❌ 获取存储信息失败:', error)
      return {
        used: 0,
        total: 0,
        available: 0,
        percentage: 0,
        formattedUsed: '未知',
        formattedTotal: '未知',
        formattedAvailable: '未知',
        type: 'Unknown'
      }
    }
  }

  /**
   * 检查是否需要清理
   * @returns {Promise<boolean>}
   */
  async shouldCleanup() {
    const info = await this.getStorageInfo()
    // 只有在使用率超过90%时才建议清理
    return parseFloat(info.percentage) > 90
  }

  /**
   * 智能清理（仅在必要时）
   * @returns {Promise<Object>}
   */
  async smartCleanup() {
    try {
      const shouldClean = await this.shouldCleanup()
      if (!shouldClean) {
        console.log('✅ 存储空间充足，无需清理')
        return {
          cleaned: false,
          reason: '存储空间充足',
          storiesDeleted: 0
        }
      }

      await this.ensureDB()

      // 获取所有故事，按访问时间排序
      const transaction = this.db.transaction(['stories'], 'readonly')
      const store = transaction.objectStore('stories')
      const allStories = await new Promise((resolve) => {
        const request = store.getAll()
        request.onsuccess = () => resolve(request.result || [])
      })

      // 按最后访问时间排序，删除最旧的
      allStories.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0))

      // 只删除最旧的20%
      const deleteCount = Math.ceil(allStories.length * 0.2)
      const toDelete = allStories.slice(-deleteCount)

      let deletedCount = 0
      for (const story of toDelete) {
        await this.deleteStory(story.id)
        deletedCount++
      }

      console.log(`🧹 清理完成，删除了 ${deletedCount} 个旧故事`)

      return {
        cleaned: true,
        reason: '存储空间不足',
        storiesDeleted: deletedCount
      }
    } catch (error) {
      console.error('❌ 清理失败:', error)
      return {
        cleaned: false,
        reason: '清理失败: ' + error.message,
        storiesDeleted: 0
      }
    }
  }

  /**
   * 格式化字节数
   */
  formatBytes(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
  }

  /**
   * 自动备份到浏览器存储（定期执行）
   */
  async autoBackup() {
    try {
      const stories = await this.getAllStories()
      const backupData = {
        version: '2.0',
        backupDate: new Date().toISOString(),
        stories: stories,
        queueStatus: this.getSaveQueueStatus()
      }
      
      // 使用localStorage的一个特殊键保存最后的备份
      // 即使IndexedDB出问题，也能从这里恢复
      try {
        const compressedBackup = JSON.stringify(backupData)
        sessionStorage.setItem('altverse-last-backup', compressedBackup)
        console.log('✅ 自动备份成功（会话存储）')
      } catch (error) {
        console.warn('⚠️ 会话存储备份失败（可能因为数据太大）:', error)
      }
      
      return true
    } catch (error) {
      console.error('❌ 自动备份失败:', error)
      return false
    }
  }

  /**
   * 从会话存储恢复数据
   */
  async restoreFromSessionBackup() {
    try {
      const backupData = sessionStorage.getItem('altverse-last-backup')
      if (!backupData) {
        console.warn('⚠️ 没有找到会话备份')
        return { success: false, message: '没有找到备份数据' }
      }
      
      const parsed = JSON.parse(backupData)
      console.log('📦 找到备份数据:', parsed.backupDate, `(${parsed.stories.length}个故事)`)
      
      // 恢复所有故事
      let restoredCount = 0
      for (const story of parsed.stories) {
        try {
          await this.saveStory(story, { retryCount: 1, timeout: 60000 })
          restoredCount++
        } catch (error) {
          console.error('❌ 恢复故事失败:', story.id, error)
        }
      }
      
      return {
        success: true,
        message: `成功恢复 ${restoredCount}/${parsed.stories.length} 个故事`,
        count: restoredCount
      }
    } catch (error) {
      console.error('❌ 从备份恢复失败:', error)
      return { success: false, message: error.message }
    }
  }

  /**
   * 导出所有数据到文件（用户手动下载）
   */
  async exportAllData() {
    try {
      const stories = await this.getAllStories()
      const exportData = {
        version: '2.0',
        exportDate: new Date().toISOString(),
        stories: stories,
        queueStatus: this.getSaveQueueStatus()
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      })

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `altverse-complete-backup-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      console.log('✅ 完整数据导出成功')
      return true
    } catch (error) {
      console.error('❌ 导出数据失败:', error)
      return false
    }
  }

  /**
   * 导入数据
   */
  async importData(jsonData) {
    try {
      const data = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData

      if (!data.stories || !Array.isArray(data.stories)) {
        throw new Error('无效的数据格式')
      }

      let importedCount = 0
      for (const story of data.stories) {
        await this.saveStory(story)
        importedCount++
      }

      console.log(`✅ 成功导入 ${importedCount} 个故事`)
      return {
        success: true,
        count: importedCount
      }
    } catch (error) {
      console.error('❌ 导入数据失败:', error)
      return {
        success: false,
        error: error.message
      }
    }
  }
}

// 导出单例
const enhancedStorageService = new EnhancedStorageService()

// 页面卸载时保存队列到sessionStorage（防止刷新丢失）
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    try {
      const queueStatus = enhancedStorageService.getSaveQueueStatus()
      if (queueStatus.pending > 0) {
        // 保存队列中的数据
        const queueData = {
          timestamp: Date.now(),
          stories: enhancedStorageService.saveQueue
        }
        sessionStorage.setItem('altverse-save-queue', JSON.stringify(queueData))
        console.log(`💾 页面卸载，已保存 ${queueStatus.pending} 个待保存故事到会话存储`)
      }
    } catch (error) {
      console.error('保存队列到会话存储失败:', error)
    }
  })
  
  // 页面加载时恢复队列
  window.addEventListener('load', async () => {
    try {
      const queueData = sessionStorage.getItem('altverse-save-queue')
      if (queueData) {
        const parsed = JSON.parse(queueData)
        const age = Date.now() - parsed.timestamp
        
        // 只恢复5分钟内的队列数据
        if (age < 5 * 60 * 1000 && Array.isArray(parsed.stories)) {
          console.log(`🔄 发现 ${parsed.stories.length} 个待保存故事，开始恢复...`)
          
          // 恢复到队列
          for (const story of parsed.stories) {
            enhancedStorageService.addToSaveQueue(story)
          }
          
          // 清除已恢复的数据
          sessionStorage.removeItem('altverse-save-queue')
          console.log('✅ 保存队列已恢复')
        } else {
          console.log('⚠️ 队列数据过期或无效，已清除')
          sessionStorage.removeItem('altverse-save-queue')
        }
      }
    } catch (error) {
      console.error('恢复保存队列失败:', error)
    }
  })
}

export default enhancedStorageService
